import { useState, useEffect } from 'react'
import DatePicker from 'react-datepicker'
import Select from 'react-select'
import * as PropTypes from 'prop-types'
import moment from 'moment-timezone'
import AsyncSelect from 'react-select/async'
import { useTranslation } from 'react-i18next'

import SearchField from '../../FormLegacy/FormFieldsV2/SearchField'

import { customStyles, DropdownIndicator } from './helpers'

const SearchComponent = ({
  search,
  setFilters,
  onSearchClick,
  loadResidentOptions,
  users,
  staffPersons,
  skip,
}) => {
  const { t } = useTranslation(['action-log', 'common'])
  const [searchingValue, setSearchingValue] = useState(search)
  const [residentsFilter, setResidentsFilter] = useState([
    { value: 'all', label: t('action-log:shared.allOption') },
  ])
  const [staffFilter, setStaffFilter] = useState([
    { value: 'all', label: t('action-log:shared.allOption') },
  ])
  const [typeFilter, setTypeFilter] = useState([
    { value: 'all', label: t('action-log:shared.allOption') },
  ])
  const [dateRange, setDateRange] = useState([
    moment(new Date().setHours(0, 0, 0, 0)).toDate(),
    moment(
      new Date(new Date().setHours(0, 0, 0, 0)).setDate(
        new Date(new Date().setHours(0, 0, 0, 0)).getDate() + 13,
      ),
    ).toDate(),
  ])
  const [startDate, endDate] = dateRange

  useEffect(() => {
    setSearchingValue(search)
  }, [search])

  const clearFromSpaces = (word) => {
    if (!word.includes(' ')) {
      setSearchingValue(word)
    }
  }

  useEffect(() => {
    setFilters({
      searchFilter: searchingValue,
      dateRangeFilter: dateRange,
      typeFilter: typeFilter,
      staffFilter: staffFilter,
      residentFilter: residentsFilter,
      skip,
    })
  }, [dateRange, typeFilter, staffFilter, residentsFilter, searchingValue])

  const clearFilters = () => {
    setSearchingValue('')
    setResidentsFilter([{ value: 'all', label: t('action-log:shared.allOption') }])
    setStaffFilter([{ value: 'all', label: t('action-log:shared.allOption') }])
    setStaffFilter([{ value: 'all', label: t('action-log:shared.allOption') }])
    setDateRange([null, null])
  }

  const selectResidents = (residentData) => {
    const valueAll = residentData.find((res) => res.value === 'all')
    if (residentData.includes(valueAll)) {
      // when input is empty and we select All
      if (residentData.length === 1 && residentData[0] === valueAll) {
        setResidentsFilter([{ value: 'all', label: t('action-log:shared.allOption') }])
        // when we select something different than All
      } else if (residentData[0] === valueAll) {
        const newData = residentData
        newData.shift()
        setResidentsFilter(newData)
      } else {
        // when we select All
        const newData = []
        newData.push({ value: 'all', label: t('action-log:shared.allOption') })
        setResidentsFilter(newData)
      }
    } else {
      setResidentsFilter(residentData)
    }
  }

  const selectStaff = (staffData) => {
    const valueAll = staffData.find((res) => res.value === 'all')
    if (staffData.includes(valueAll)) {
      // when input is empty and we select All
      if (staffData.length === 1 && staffData[0] === valueAll) {
        setStaffFilter([{ value: 'all', label: t('action-log:shared.allOption') }])
        // when we select something different than All
      } else if (staffData[0] === valueAll) {
        const newData = staffData
        newData.shift()
        setStaffFilter(newData)
      } else {
        // when we select All
        const newData = []
        newData.push({ value: 'all', label: t('action-log:shared.allOption') })
        setStaffFilter(newData)
      }
    } else {
      setStaffFilter(staffData)
    }
  }

  const selectType = (types) => {
    const valueAll = types.find((res) => res.value === 'all')
    if (types.includes(valueAll)) {
      // when input is empty and we select All
      if (types.length === 1 && types[0] === valueAll) {
        setTypeFilter([{ value: 'all', label: t('action-log:shared.allOption') }])
        // when we select something different than All
      } else if (types[0] === valueAll) {
        const newData = types
        newData.shift()
        setTypeFilter(newData)
      } else {
        // when we select All
        const newData = []
        newData.push({ value: 'all', label: t('action-log:shared.allOption') })
        setTypeFilter(newData)
      }
    } else {
      setTypeFilter(types)
    }
  }

  return (
    <div className="ctp-action-log-flex-container__search">
      <div className="tab-header">{t('action-log:searchComponent.advancedFiltersHeader')}</div>
      <div className="filters">
        <SearchField
          placeholder={t('common:search.placeholder')}
          value={searchingValue}
          onChange={(e) => clearFromSpaces(e.target.value)}
        />
        <div className="date-picker-container">
          <label>{t('action-log:searchComponent.dateRangeLabel')}</label>
          <DatePicker
            className={'input'}
            selectsRange={true}
            startDate={startDate}
            endDate={endDate}
            placeholderText={t('action-log:searchComponent.dateRangePlaceholder')}
            isClearable={true}
            onChange={(update) => {
              setDateRange(update)
            }}
          />
        </div>
        <div className="type-picker-container">
          <label>{t('action-log:shared.typeLabel')}</label>
          <Select
            defaultValue={[]}
            isMulti
            placeholder={t('action-log:searchComponent.typePlaceholder')}
            name="type"
            clearValue
            options={[
              {
                value: 'all',
                label: t('action-log:shared.allOption'),
              },
              {
                value: 'shiftNote',
                label: t('action-log:shared.shiftNoteLabel'),
              },
              {
                value: 'checkIn',
                label: t('action-log:searchComponent.checkInOption'),
              },
            ]}
            styles={customStyles}
            className="basic-multi-select"
            classNamePrefix="select"
            components={{ DropdownIndicator }}
            value={typeFilter}
            onChange={(e) => selectType(e)}
          />
        </div>
        <div className="staff-picker-container">
          <label>{t('action-log:shared.staffLabel')}</label>
          <Select
            defaultValue={[]}
            isMulti
            placeholder={t('action-log:searchComponent.staffPlaceholder')}
            name="staff"
            clearValue
            options={[{ value: 'all', label: t('action-log:shared.allOption') }, ...staffPersons]}
            styles={customStyles}
            className="basic-multi-select"
            classNamePrefix="select"
            components={{ DropdownIndicator }}
            value={staffFilter}
            onChange={(e) => selectStaff(e)}
          />
        </div>
        <div className="resident-picker-container">
          <label>{t('action-log:shared.residentLabel')}</label>
          <AsyncSelect
            loadOptions={loadResidentOptions}
            defaultOptions={[{ value: 'all', label: t('action-log:shared.allOption') }, ...users]}
            isMulti
            placeholder={t('action-log:shared.searchResidentPlaceholder')}
            name="resident"
            clearValue
            styles={customStyles}
            className="basic-multi-select"
            classNamePrefix="select"
            components={{ DropdownIndicator }}
            value={residentsFilter}
            onChange={(e) => selectResidents(e)}
          />
        </div>
      </div>
      <div className="column-buttons">
        <div className="clear-filters-button">
          <button className="button button-gray full-width" onClick={() => clearFilters()}>
            {t('action-log:searchComponent.clearFiltersButton')}
          </button>
        </div>
        <div className="search-button">
          <button className="button button-primary" onClick={onSearchClick}>
            {t('action-log:searchComponent.searchButton')}
          </button>
        </div>
      </div>
    </div>
  )
}

SearchComponent.propTypes = {
  search: PropTypes.string,
  setFilters: PropTypes.func,
  onSearchClick: PropTypes.func,
  loadResidentOptions: PropTypes.func,
  users: PropTypes.array,
  staffPersons: PropTypes.array,
  skip: PropTypes.number,
}

export default SearchComponent
